/**
 * BubbleCloud — jiggly floating tag picker used in the journey onboarding slide.
 *
 * Props:
 *   initialSelected  {string[]}   tag IDs pre-selected (e.g. from saved prefs)
 *   onConfirm        {function}   called with string[] when user taps "Set My Journey"
 *                                 For curated journeys: called with ['curated:<id>']
 */
import React, { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { JOURNEY_TAGS } from '../utils/journeyEngine';
import { CURATED_JOURNEYS } from '../utils/curatedJourneys';

// Tags to show in the "build your own" section
const VISIBLE_TAGS = JOURNEY_TAGS.filter(
  (t) => ![
    'casual-fun', 'quick-sessions', 'competitive', 'just-explore',
    'young-learner', 'breath-control', 'classical-focus', 'prepare-performance',
  ].includes(t.id)
);

// Per-bubble animation params — deterministic so there's no layout jump on re-render
function animParams(index) {
  const durs =  [3.4, 4.1, 3.7, 4.8, 3.2, 4.5, 3.9, 4.3, 3.6, 5.0,
                 4.0, 3.5, 4.7, 3.8, 4.4, 3.3, 4.9, 3.6, 4.2, 3.8];
  const dxs  =  [ 5, -6,  4, -5,  7, -4,  6, -7,  5, -6,
                  4, -5,  7, -3,  6, -6,  4, -7,  5, -4];
  const dys  =  [ 9,  7, 11,  8, 10, 12,  7,  9, 11,  8,
                 10,  7, 12,  9,  8, 11,  7, 10,  9, 11];
  const delays = durs.map((d, i) => -((i * 0.43) % d));
  return { dur: durs[index % 20], dx: dxs[index % 20], dy: dys[index % 20], delay: delays[index % 20] };
}

// Inject one shared @keyframes block once (avoids repeating it per bubble)
const KEYFRAME_ID = '__bubble_float_kf__';
function ensureKeyframes() {
  if (document.getElementById(KEYFRAME_ID)) return;
  const style = document.createElement('style');
  style.id = KEYFRAME_ID;
  style.textContent = `
    @keyframes bubbleFloat {
      0%   { transform: var(--bf-t0); }
      50%  { transform: var(--bf-t1); }
      100% { transform: var(--bf-t0); }
    }
    @keyframes bubbleFloatSel {
      0%   { transform: var(--bfS-t0); }
      50%  { transform: var(--bfS-t1); }
      100% { transform: var(--bfS-t0); }
    }
  `;
  document.head.appendChild(style);
}

const MAX_SELECT = 5;

const BubbleCloud = ({ initialSelected = [], onConfirm }) => {
  const [selected, setSelected] = useState(() => new Set(initialSelected));
  const prevInitRef = useRef(initialSelected.join(','));

  // Re-sync when carousel re-opens with different saved prefs
  useEffect(() => {
    const key = initialSelected.join(',');
    if (key !== prevInitRef.current) {
      prevInitRef.current = key;
      setSelected(new Set(initialSelected));
    }
  });

  useEffect(() => {
    ensureKeyframes();
  }, []);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else if (next.size < MAX_SELECT) {
        next.add(id);
      }
      return next;
    });
  };

  const canConfirm = selected.size >= 2;
  const remaining = Math.max(0, 2 - selected.size);

  // ── Curated journey card pulse animation ─────────────────────────────────
  useEffect(() => {
    if (document.getElementById('__curated_pulse_kf__')) return;
    const style = document.createElement('style');
    style.id = '__curated_pulse_kf__';
    style.textContent = `
      @keyframes curatedPulse {
        0%   { box-shadow: var(--cp-s0); }
        50%  { box-shadow: var(--cp-s1); }
        100% { box-shadow: var(--cp-s0); }
      }
      @keyframes curatedShimmer {
        0%   { background-position: -200% center; }
        100% { background-position: 200% center; }
      }
    `;
    document.head.appendChild(style);
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        height: '100%',
        minHeight: 0,
        px: 2,
        pt: 0,
        pb: 0,
      }}
    >
      {/* Header */}
      <Typography
        variant="h5"
        sx={{ fontWeight: 'bold', color: '#fff', mt: 2, mb: 0.5, textAlign: 'center', lineHeight: 1.2, flexShrink: 0 }}
      >
        What calls to you?
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: 'rgba(255,255,255,0.75)', mb: 1.5, textAlign: 'center', flexShrink: 0 }}
      >
        Choose a path curated by our in-house experts
      </Typography>

      {/* ── Featured Curated Journey Cards ───────────────────────────────── */}
      <Box sx={{ width: '100%', mt: 1, mb: 1.5, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
          {CURATED_JOURNEYS.map((journey) => (
            <Box
              key={journey.id}
              component="button"
              onClick={() => onConfirm([`curated:${journey.id}`])}
              sx={{
                flex: '1 1 0',
                minWidth: 90,
                maxWidth: 140,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.4,
                px: 1.5,
                py: 1.0,
                borderRadius: 3,
                border: '2px solid rgba(255,255,255,0.35)',
                background: journey.gradient,
                backgroundSize: '200% auto',
                color: '#fff',
                fontWeight: 800,
                cursor: 'pointer',
                userSelect: 'none',
                WebkitTapHighlightColor: 'transparent',
                outline: 'none',
                '--cp-s0': `0 0 14px 3px ${journey.glowColor}, 0 2px 10px rgba(0,0,0,0.25)`,
                '--cp-s1': `0 0 28px 8px ${journey.glowColor}, 0 4px 16px rgba(0,0,0,0.35)`,
                animation: 'curatedPulse 2.8s ease-in-out infinite',
                transition: 'transform 0.15s',
                '&:active': { transform: 'scale(0.96)' },
              }}
            >
              <span style={{ fontSize: '1.5rem', lineHeight: 1 }} aria-hidden="true">{journey.emoji}</span>
              <span style={{ fontSize: '0.82rem', fontWeight: 800, letterSpacing: 0.3 }}>{journey.name}</span>
              <span style={{ fontSize: '0.62rem', opacity: 0.85, fontWeight: 500, textAlign: 'center', lineHeight: 1.2 }}>
                {journey.tagline}
              </span>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Divider */}
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mt: 1, mb: 1.5, flexShrink: 0, gap: 1 }}>
        <Box sx={{ flex: 1, height: '1px', backgroundColor: 'rgba(255,255,255,0.2)' }} />
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', whiteSpace: 'nowrap' }}>
          or pick your own flavour
        </Typography>
        <Box sx={{ flex: 1, height: '1px', backgroundColor: 'rgba(255,255,255,0.2)' }} />
      </Box>

      {/* Bubble grid — scrollable only if truly needed */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          width: '100%',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.2,
          justifyContent: 'center',
          alignContent: 'center',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          pt: 0.5,
          pb: 0.5,
        }}
      >
        {/* First half of visible tags */}
        {VISIBLE_TAGS.slice(0, Math.ceil(VISIBLE_TAGS.length / 2)).map((tag, i) => {
          const isSel = selected.has(tag.id);
          const { dur, dx, dy, delay } = animParams(i);
          const t0 = 'translateY(0px) translateX(0px)';
          const t1Norm = `translateY(-${dy}px) translateX(${dx}px)`;
          const t0sel = 'translateY(0px) translateX(0px) scale(1.08)';
          const t1sel = `translateY(-${dy}px) translateX(${dx}px) scale(1.08)`;

          return (
            <Box
              key={tag.id}
              component="button"
              onClick={() => toggle(tag.id)}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 2,
                py: 0.6,
                borderRadius: 99,
                border: isSel ? '2px solid #fff' : '2px solid rgba(255,255,255,0.4)',
                backgroundColor: isSel ? '#fff' : 'rgba(255,255,255,0.15)',
                color: isSel ? '#222' : '#fff',
                fontWeight: 600,
                fontSize: { xs: '0.75rem', sm: '0.85rem' },
                cursor: 'pointer',
                userSelect: 'none',
                WebkitTapHighlightColor: 'transparent',
                outline: 'none',
                boxShadow: isSel
                  ? '0 4px 16px rgba(0,0,0,0.28), 0 0 0 3px rgba(255,255,255,0.3)'
                  : '0 2px 8px rgba(0,0,0,0.12)',
                transition: 'background-color 0.18s, color 0.18s, border-color 0.18s, box-shadow 0.18s',
                '--bf-t0':  t0,
                '--bf-t1':  t1Norm,
                '--bfS-t0': t0sel,
                '--bfS-t1': t1sel,
                animation: isSel
                  ? `bubbleFloatSel ${dur}s ease-in-out ${delay}s infinite`
                  : `bubbleFloat ${dur}s ease-in-out ${delay}s infinite`,
              }}
            >
              <span aria-hidden="true">{tag.emoji}</span>
              <span>{tag.label}</span>
            </Box>
          );
        })}

        {/* ── Beta Tester bubble ── */}
        <Box
          component="button"
          onClick={() => onConfirm(['beta-tester'])}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.6,
            px: 2.4,
            py: 0.6,
            borderRadius: 99,
            border: '2px solid #ff6b35',
            background: 'linear-gradient(135deg, #ff6b35 0%, #f7c948 100%)',
            color: '#1a0a00',
            fontWeight: 800,
            fontSize: { xs: '0.75rem', sm: '0.85rem' },
            cursor: 'pointer',
            userSelect: 'none',
            WebkitTapHighlightColor: 'transparent',
            outline: 'none',
            boxShadow: '0 0 20px 5px rgba(255,107,53,0.6), 0 2px 10px rgba(0,0,0,0.25)',
            letterSpacing: 0.3,
            '--bf-t0': 'translateY(0px) translateX(0px)',
            '--bf-t1': 'translateY(-8px) translateX(3px)',
            animation: 'bubbleFloat 3.0s ease-in-out -0.5s infinite',
            transition: 'box-shadow 0.2s',
            '&:active': { boxShadow: '0 0 32px 10px rgba(255,107,53,0.9)' },
          }}
        >
          <span aria-hidden="true">🔬</span>
          <span>Beta Tester (unlock everything)</span>
        </Box>

        {/* Second half of visible tags */}
        {VISIBLE_TAGS.slice(Math.ceil(VISIBLE_TAGS.length / 2)).map((tag, i) => {
          const isSel = selected.has(tag.id);
          const { dur, dx, dy, delay } = animParams(i + 9);
          const t0 = 'translateY(0px) translateX(0px)';
          const t1Norm = `translateY(-${dy}px) translateX(${dx}px)`;
          const t0sel = 'translateY(0px) translateX(0px) scale(1.08)';
          const t1sel = `translateY(-${dy}px) translateX(${dx}px) scale(1.08)`;

          return (
            <Box
              key={tag.id}
              component="button"
              onClick={() => toggle(tag.id)}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 2,
                py: 0.6,
                borderRadius: 99,
                border: isSel ? '2px solid #fff' : '2px solid rgba(255,255,255,0.4)',
                backgroundColor: isSel ? '#fff' : 'rgba(255,255,255,0.15)',
                color: isSel ? '#222' : '#fff',
                fontWeight: 600,
                fontSize: { xs: '0.75rem', sm: '0.85rem' },
                cursor: 'pointer',
                userSelect: 'none',
                WebkitTapHighlightColor: 'transparent',
                outline: 'none',
                boxShadow: isSel
                  ? '0 4px 16px rgba(0,0,0,0.28), 0 0 0 3px rgba(255,255,255,0.3)'
                  : '0 2px 8px rgba(0,0,0,0.12)',
                transition: 'background-color 0.18s, color 0.18s, border-color 0.18s, box-shadow 0.18s',
                '--bf-t0':  t0,
                '--bf-t1':  t1Norm,
                '--bfS-t0': t0sel,
                '--bfS-t1': t1sel,
                animation: isSel
                  ? `bubbleFloatSel ${dur}s ease-in-out ${delay}s infinite`
                  : `bubbleFloat ${dur}s ease-in-out ${delay}s infinite`,
              }}
            >
              <span aria-hidden="true">{tag.emoji}</span>
              <span>{tag.label}</span>
            </Box>
          );
        })}
      </Box>

      {/* Counter + CTA */}
      <Box sx={{ width: '100%', textAlign: 'center', pt: 1, pb: 2, flexShrink: 0 }}>
        <Typography
          variant="caption"
          sx={{ color: 'rgba(255,255,255,0.7)', mb: 0.5, display: 'block' }}
        >
          {remaining > 0
            ? `Pick ${remaining} more to continue`
            : `${selected.size} selected — looks great!`}
        </Typography>
        <Button
          variant="contained"
          disabled={!canConfirm}
          onClick={() => onConfirm(Array.from(selected))}
          sx={{
            backgroundColor: canConfirm ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.22)',
            color: canConfirm ? '#1a1a1a' : 'rgba(255,255,255,0.45)',
            fontWeight: 'bold',
            fontSize: '1rem',
            px: 4,
            py: 0.9,
            borderRadius: 99,
            boxShadow: canConfirm ? '0 4px 20px rgba(0,0,0,0.22)' : 'none',
            textTransform: 'none',
            transition: 'all 0.2s',
            '&:hover': {
              backgroundColor: canConfirm ? '#fff' : 'rgba(255,255,255,0.22)',
              boxShadow: canConfirm ? '0 6px 24px rgba(0,0,0,0.3)' : 'none',
            },
            '&.Mui-disabled': {
              backgroundColor: 'rgba(255,255,255,0.22)',
              color: 'rgba(255,255,255,0.45)',
            },
          }}
        >
          Set My Journey ✨
        </Button>
      </Box>
    </Box>
  );
};

export default BubbleCloud;
